import { ConvertedCourseData, CourseBuilderBlock, CourseOutlineData, CourseStepData } from '@modules/common/CourseOutlineModal/CourseOutlineModal';

/**
 * Read DOCX file using JavaScript with style information
 */
export async function readDocxFile(file: File): Promise<any> {
  return new Promise((resolve, reject) => {
    // Check if we have mammoth.js available
    if (typeof (window as any).mammoth === 'undefined') {
      // Fallback: Load mammoth.js dynamically
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/mammoth@1.6.0/mammoth.browser.js';
      script.onload = () => {
        extractStructuredDataFromDocx(file, resolve, reject);
      };
      script.onerror = () => {
        reject(new Error('Failed to load DOCX processor'));
      };
      document.head.appendChild(script);
    } else {
      extractStructuredDataFromDocx(file, resolve, reject);
    }
  });
}

/**
 * Extract structured data from DOCX file
 */
function extractStructuredDataFromDocx(file: File, resolve: (data: any) => void, reject: (error: Error) => void) {
  const reader = new FileReader();
  reader.onload = e => {
    const arrayBuffer = e.target?.result as ArrayBuffer;

    // Extract HTML with better style mapping to preserve formatting including lists
    const styleMap = [
      "p[style-name='Heading 1'] => h1:fresh",
      "p[style-name='Heading 2'] => h2:fresh",
      "p[style-name='Heading 3'] => h3:fresh",
      "p[style-name='Heading 4'] => h4:fresh",
      "p[style-name='Heading 5'] => h5:fresh",
      "p[style-name='Heading 6'] => h6:fresh",
      'b => strong',
      'i => em',
      'u => u',
    ];

    Promise.all([
      (window as any).mammoth.extractRawText({ arrayBuffer }),
      (window as any).mammoth.convertToHtml({
        arrayBuffer,
        styleMap: styleMap,
      }),
    ])
      .then(([textResult, htmlResult]: any[]) => {
        resolve({
          text: textResult.value,
          html: htmlResult.value,
          messages: [...textResult.messages, ...htmlResult.messages],
        });
      })
      .catch((error: any) => {
        reject(error);
      });
  };
  reader.onerror = () => {
    reject(new Error('Failed to read file'));
  };
  reader.readAsArrayBuffer(file);
}

/**
 * Parse course structure from HTML and text data with preserved formatting
 */
export function parseCourseStructure(data: any): CourseOutlineData {
  const { text, html } = data;

  // Parse HTML to get heading structure
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Extract course title
  let courseTitle = 'Untitled Course';
  const titleElements: NodeListOf<Element> = doc.querySelectorAll('h1, h2, p');
  for (const el of Array.from(titleElements)) {
    const titleText = el.textContent?.trim() || '';
    // Exclude section patterns (1. or 1.0) and step patterns (1.1, 1.2, etc.), allowing leading icons
    const isSectionPattern = titleText.match(/^[^\d]*?\d+(?:\.0|\.) /);
    const isStepPattern = titleText.match(/^[^\d]*?\d+\.\d+\s/);

    if (titleText.length > 10 && titleText.length < 100 && !isSectionPattern && !isStepPattern) {
      courseTitle = titleText;
      break;
    }
  }

  const sections: CourseOutlineData['sections'] = [];
  let currentSection: any = null;
  let currentStep: any = null;

  // Split text into lines for section/step detection
  const lines = text
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0);

  // Also process HTML to preserve formatted content
  const htmlLines = html.split('\n');
  const htmlContentBuffer: string[] = [];
  const isCapturingStepContent = false;
  const stepContentStart = -1;

  // First, process HTML elements for H2 (sections) and H3 (steps)
  const htmlElements = doc.querySelectorAll('h2, h3');
  const htmlStructure = Array.from(htmlElements)
    .map((el, index) => {
      const textContent = el.textContent?.trim() || '';
      // Allow leading icons/emojis before section/step numbers
      const isSectionOrStep = textContent.match(/^[^\d]*?(\d+)(?:\.0|\.) .+$/) || textContent.match(/^[^\d]*?(\d+\.\d+)\s+.+$/);
      // Ignore non-matching headings
      if (!isSectionOrStep) {
        return null;
      }
      return {
        type: el.tagName.toLowerCase(),
        text: textContent,
        index: index,
      };
    })
    .filter(el => el !== null);

  // Process HTML structure first
  for (let i = 0; i < htmlStructure.length; i++) {
    const element = htmlStructure[i];

    if (element.type === 'h2') {
      // Check if H2 matches section pattern (allows leading icons like emojis): e.g., "🚀 1. Title" or "✨ 1.0 Title"
      const h2SectionMatch = element.text.match(/^[^\d]*?(\d+)(?:\.0|\.) (.+)$/);
      if (h2SectionMatch && parseInt(h2SectionMatch[1]) <= 20) {
        // Save previous section
        if (currentSection) {
          sections.push(currentSection);
        }

        const sectionNumber = h2SectionMatch[1];
        const sectionName = h2SectionMatch[2].trim();

        currentSection = {
          id: `section-${sectionNumber}`,
          name: sectionName,
          steps: [],
        };
        currentStep = null;
        continue;
      }
    } else if (element.type === 'h3' && currentSection) {
      // Check if H3 matches step pattern (allows leading icons like emojis): e.g., "🚀 6.1 Step Title"
      const h3StepMatch = element.text.match(/^[^\d]*?(\d+\.\d+)\s+(.+)$/);
      if (h3StepMatch) {
        const stepNumber = h3StepMatch[1];
        const stepName = h3StepMatch[2].trim();

        // Extract content for this step (content between this H3 and next H2/H3)
        const nextHeadingText = i + 1 < htmlStructure.length ? htmlStructure[i + 1].text : null;
        const stepContent = extractHtmlStepContent(html, element.text, nextHeadingText);

        currentStep = {
          id: `step-${stepNumber}`,
          name: stepName,
          content: stepContent,
        };

        currentSection.steps.push(currentStep);
        continue;
      }
    }
  }

  // Continue with text-based parsing if no HTML structure was found, or to supplement it
  const hasHtmlStructure = sections.length > 0;

  //   for (let i = 0; i < lines.length; i++) {
  //     const line = lines[i];

  //     // Check for main section headers (1. Introduction... or 1.0 Introduction...)
  //     const sectionMatch = line.match(/^(\d+)(?:\.0|\.) (.+)$/);
  //     if (sectionMatch && parseInt(sectionMatch[1]) <= 20) {
  //       // Skip if this section was already processed from HTML
  //       const sectionNumber = sectionMatch[1];
  //       const existingSection = sections.find(s => s.id === `section-${sectionNumber}`);
  //       if (hasHtmlStructure && existingSection) {
  //         currentSection = existingSection;
  //         continue;
  //       }
  //       // Save any accumulated HTML content for previous step
  //       if (currentStep && isCapturingStepContent) {
  //         currentStep.content = extractFormattedContent(html, stepContentStart, i, lines);
  //         isCapturingStepContent = false;
  //       }

  //       // Save previous section
  //       if (currentSection) {
  //         sections.push(currentSection);
  //       }

  //       // Create new section (only if not already processed from HTML)
  //       const sectionName = sectionMatch[2].trim();

  //       currentSection = {
  //         id: `section-${sectionNumber}`,
  //         name: sectionName,
  //         steps: [],
  //       };
  //       currentStep = null;
  //       isCapturingStepContent = false;
  //       continue;
  //     }

  //     // Check for subsection/step headers (1.1 Why Week 2...)
  //     const stepMatch = line.match(/^(\d+\.\d+)\s+(.+)$/);
  //     if (stepMatch && currentSection) {
  //       const stepNumber = stepMatch[1];
  //       const stepName = stepMatch[2].trim();

  //       // Skip if this step was already processed from HTML
  //       const existingStep = currentSection.steps.find(s => s.id === `step-${stepNumber}`);
  //       if (hasHtmlStructure && existingStep) {
  //         currentStep = existingStep;
  //         continue;
  //       }

  //       // Save any accumulated HTML content for previous step
  //       if (currentStep && isCapturingStepContent) {
  //         currentStep.content = extractFormattedContent(html, stepContentStart, i, lines);
  //       }

  //       currentStep = {
  //         id: `step-${stepNumber}`,
  //         name: stepName,
  //         content: '',
  //       };

  //       currentSection.steps.push(currentStep);

  //       // Start capturing content for this step
  //       isCapturingStepContent = true;
  //       stepContentStart = i + 1;
  //       continue;
  //     }
  //   }

  // Save final step content
  if (currentStep && isCapturingStepContent) {
    currentStep.content = extractFormattedContent(html, stepContentStart, lines.length, lines);
  }

  // Add the last section
  if (currentSection) {
    sections.push(currentSection);
  }

  return {
    title: courseTitle,
    sections,
  };
}

/**
 * Helper function to extract content for HTML-based steps
 * Extracts all content between current step heading and next heading (section or step)
 */
function extractHtmlStepContent(html: string, stepHeadingText: string, nextHeadingText: string | null): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Find the current step heading (could be H3 or any heading with step pattern)
  const allHeadings = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');
  let currentHeading: Element | null = null;
  let nextHeading: Element | null = null;

  // Find current heading by text content
  for (const heading of Array.from(allHeadings)) {
    if (heading.textContent?.trim() === stepHeadingText) {
      currentHeading = heading;
      break;
    }
  }

  if (!currentHeading) {
    console.warn('Current heading not found:', stepHeadingText);
    return '';
  }

  // Find next heading by text content (if provided)
  if (nextHeadingText) {
    for (const heading of Array.from(allHeadings)) {
      if (heading.textContent?.trim() === nextHeadingText) {
        nextHeading = heading;
        break;
      }
    }
  }

  // Get all elements in the document
  const allElements = doc.body.children;
  const elementsArray = Array.from(allElements);

  // Find indices of current and next headings
  const currentIndex = elementsArray.indexOf(currentHeading);
  const nextIndex = nextHeading ? elementsArray.indexOf(nextHeading) : elementsArray.length;

  if (currentIndex === -1) {
    console.warn('Current heading not found in elements array');
    return '';
  }

  // Extract all elements between current and next heading
  let content = '';

  for (let i = currentIndex + 1; i < nextIndex; i++) {
    const element = elementsArray[i];
    if (element) {
      // Include ALL tags: h1, h2, h3, p, span, ul, code, etc.
      // Only exclude other section/step headings

      content += element.outerHTML + '\n';
      //   const tagName = element.tagName.toLowerCase();
      //   const elementText = element.textContent?.trim() || '';

      //   // Check if this element is a section or step heading itself (with icon support)
      //   const isSectionHeading = elementText.match(/^[^\d]*?\d+(?:\.0|\.) /);
      //   const isStepHeading = elementText.match(/^[^\d]*?\d+\.\d+\s/);

      //   // Include everything except other section/step headings
      //   if (!isSectionHeading && !isStepHeading) {
      //     content += element.outerHTML + '\n';
      //   } else {
      //     // If we encounter another section/step heading before the expected next heading,
      //     // stop here to avoid including content from other sections/steps
      //     console.log('Stopping at unexpected heading:', elementText);
      //     break;
      //   }
    }
  }

  return content.trim();
}

/**
 * Helper function to extract formatted content from HTML
 */
export function extractFormattedContent(html: string, startLine: number, endLine: number, textLines: string[]): string {
  if (startLine >= endLine || startLine < 0) return '';

  // Get the text content that we want to find in HTML
  //   const targetContent = textLines.slice(startLine, endLine).join('\n');
  const targetContent = textLines.slice(startLine, endLine).join('');

  if (!targetContent.trim()) return '';

  // Parse HTML and find matching content with formatting
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  let formattedContent = '';
  const elements: NodeListOf<Element> = doc.querySelectorAll('p, h1, h2, h3, h4, h5, h6, ul, ol');
  const processedElements = new Set();

  // Look for elements that contain our target content
  for (const element of Array.from(elements)) {
    if (processedElements.has(element)) continue;

    const elementText = element.textContent?.trim() || '';

    // More flexible matching - check if ANY part of the element text is in target content
    const isMatch =
      targetContent.includes(elementText) || elementText.split('\n').some(line => line.trim().length > 5 && targetContent.includes(line.trim()));

    if (isMatch && elementText.length > 3) {
      const tagName = element.tagName.toLowerCase();

      // Get the HTML content with formatting preserved
      let elementHtml = element.outerHTML;

      // Clean up and format the HTML while preserving lists
      elementHtml = elementHtml
        .replace(/<strong>/g, '<strong>') // Keep strong tags
        .replace(/<\/strong>/g, '</strong>')
        .replace(/<em>/g, '<em>') // Keep em tags
        .replace(/<\/em>/g, '</em>')
        .replace(/<u>/g, '<u>') // Keep underline tags
        .replace(/<\/u>/g, '</u>')
        .replace(/<code>/g, '<code>') // Keep code tags
        .replace(/<\/code>/g, '</code>')
        .replace(/<ul>/g, '<ul>') // Keep unordered lists
        .replace(/<\/ul>/g, '</ul>')
        .replace(/<ol>/g, '<ol>') // Keep ordered lists
        .replace(/<\/ol>/g, '</ol>')
        .replace(/<li>/g, '<li>') // Keep list items
        .replace(/<\/li>/g, '</li>')
        .trim();

      if (elementHtml) {
        if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
          // Extract inner HTML and wrap in strong tags
          const innerHtml = element.innerHTML;
          formattedContent += `<strong>${innerHtml}</strong>\n\n`;
        } else if (['ul', 'ol'].includes(tagName)) {
          // Include the entire list structure
          formattedContent += `${elementHtml}\n\n`;
        } else {
          // Regular paragraph - extract inner HTML
          const innerHtml = element.innerHTML;
          formattedContent += `${innerHtml}\n\n`;
        }
        processedElements.add(element);
      }
    }
  }

  // If we couldn't match HTML content, fall back to comprehensive text processing
  if (!formattedContent.trim()) {
    const contentLines = targetContent.split('\n');
    let currentListType = null; // 'ul' for unordered, 'ol' for ordered
    let listItems = [];

    for (const line of contentLines) {
      const trimmedLine = line.trim();
      if (!trimmedLine) continue;

      // Check for ordered list items (1., 2., 3., etc.)
      const orderedMatch = trimmedLine.match(/^(\d+)\.(\s+)(.+)$/);
      if (orderedMatch) {
        if (currentListType !== 'ol') {
          // Close previous unordered list if exists
          if (currentListType === 'ul' && listItems.length > 0) {
            formattedContent += '<ul>' + listItems.map(item => `<li>${processInlineFormatting(item)}</li>`).join('') + '</ul>\n\n';
          }
          currentListType = 'ol';
          listItems = [];
        }
        listItems.push(orderedMatch[3]);
        continue;
      }

      // Check for unordered list items (various bullets)
      const unorderedMatch = trimmedLine.match(/^[•·⁃\-\*∙]\s+(.+)$/);
      if (unorderedMatch) {
        if (currentListType !== 'ul') {
          // Close previous ordered list if exists
          if (currentListType === 'ol' && listItems.length > 0) {
            formattedContent += '<ol>' + listItems.map(item => `<li>${processInlineFormatting(item)}</li>`).join('') + '</ol>\n\n';
          }
          currentListType = 'ul';
          listItems = [];
        }
        listItems.push(unorderedMatch[1]);
        continue;
      }

      // This line is not a list item - close any open list
      if (currentListType && listItems.length > 0) {
        const listTag = currentListType;
        formattedContent += `<${listTag}>` + listItems.map(item => `<li>${processInlineFormatting(item)}</li>`).join('') + `</${listTag}>\n\n`;
        currentListType = null;
        listItems = [];
      }

      // Process as regular content
      if (trimmedLine.match(/^\d+\./)) {
        // Looks like a heading
        formattedContent += `<strong>${processInlineFormatting(trimmedLine)}</strong>\n\n`;
      } else {
        // Regular paragraph
        formattedContent += processInlineFormatting(trimmedLine) + '\n\n';
      }
    }

    // Close any remaining list
    if (currentListType && listItems.length > 0) {
      const listTag = currentListType;
      formattedContent += `<${listTag}>` + listItems.map(item => `<li>${processInlineFormatting(item)}</li>`).join('') + `</${listTag}>\n\n`;
    }
  }

  return formattedContent.trim();
}

/**
 * Helper function to process inline formatting
 */
export function processInlineFormatting(text: string): string {
  return text
    .replace(/`([^`]+)`/g, '<code>$1</code>') // Code in backticks
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>') // Bold text
    .replace(/\*([^*]+)\*/g, '<em>$1</em>') // Italic text
    .trim();
}

/**
 * Helper function to parse formatted content into blocks (paragraph only)
 */
export function parseContentIntoBlocks(htmlContent: string, startBlockIndex: number): CourseBuilderBlock[] {
  const blocks: CourseBuilderBlock[] = [];
  let blockIndex = startBlockIndex;

  if (!htmlContent || !htmlContent.trim()) return blocks;

  // Split content by double line breaks to separate paragraphs
  const contentParts = htmlContent
    .split('\n\n')
    .map(part => part.trim())
    .filter(part => part.length > 0);

  for (const part of Array.from([htmlContent])) {
    if (!part.trim()) continue;

    // Everything becomes a paragraph block - headings are just bold text within paragraphs
    blocks.push({
      id: `block-${Date.now()}-${blockIndex++}`,
      type: 'text',
      variation: 'paragraph',
      data: {
        paragraph: part.split('\n').join('<br/>'), // Preserve line breaks within paragraphs
        columnsWidths: '1',
      },
    });
  }

  // If no blocks were created from formatted content, create one from raw content
  if (blocks.length === 0 && htmlContent.trim()) {
    blocks.push({
      id: `block-${Date.now()}-${blockIndex++}`,
      type: 'text',
      variation: 'paragraph',
      data: {
        paragraph: htmlContent,
        columnsWidths: '1',
      },
    });
  }

  return blocks;
}

/**
 * Convert parsed course outline into course builder block structure
 */
export function convertCourseToBlocks(courseOutline: CourseOutlineData): ConvertedCourseData {
  const convertedSections = courseOutline.sections.map(section => {
    const convertedSteps = section.steps.map(step => {
      const blocks: CourseBuilderBlock[] = [];
      let blockIndex = 0;

      // Create heading block for step name
      if (step.name && step.name.trim()) {
        blocks.push({
          id: `block-${Date.now()}-${blockIndex++}`,
          type: 'text',
          variation: 'heading',
          data: {
            heading: step.name.trim(),
            columnsWidths: '1',
          },
        });
      }

      // Process step content and convert to blocks with preserved formatting
      if (step.content && step.content.trim()) {
        // Parse the HTML content to create structured blocks
        const formattedBlocks = parseContentIntoBlocks(step.content, blockIndex);
        blocks.push(...formattedBlocks);
        blockIndex += formattedBlocks.length;
      }

      // If no blocks were created, create a basic paragraph block with step name
      if (blocks.length === 0) {
        blocks.push({
          id: `block-${Date.now()}-${blockIndex++}`,
          type: 'text',
          variation: 'paragraph',
          data: {
            paragraph: step.name || 'No content available',
            columnsWidths: '1',
          },
        });
      }

      const stepData: CourseStepData = {
        title: step.name,
        blocks,
      };

      return {
        id: step.id,
        name: step.name,
        content: step.content || '',
        stepData,
      };
    });

    return {
      id: section.id,
      name: section.name,
      steps: convertedSteps,
    };
  });

  return {
    title: courseOutline.title,
    sections: convertedSections,
  };
}
